import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { School, Monitor, ClipboardCheck, CheckCircle2 } from 'lucide-react';
import { features } from '../mock';

const iconMap = {
  School, Monitor, ClipboardCheck
};

const Features = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Page Header */}
      <section className="bg-gradient-to-b from-red-50 to-white py-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 items-center max-w-5xl mx-auto">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Learning Modes</h1>
              <p className="text-xl text-gray-600">
                Choose the learning style that fits your schedule and preferences - we've got you covered
              </p>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1722573783625-eceb04251036?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxpbmRpYW4lMjBnb3Zlcm5tZW50JTIwZXhhbSUyMGhhbGwlMjBleGFtaW5hdGlvbnxlbnwwfHx8fDE3NzI2OTQ1OTN8MA&ixlib=rb-4.1.0&q=85" 
                alt="Examination Hall" 
                className="rounded-2xl shadow-xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
            {features.map((feature) => {
              const Icon = iconMap[feature.icon];
              return (
                <Card key={feature.id} className="text-center hover:shadow-xl transition-all border-2 hover:border-red-200">
                  <CardHeader>
                    <div className="flex justify-center mb-4">
                      <div className="bg-red-100 p-6 rounded-full">
                        <Icon className="text-red-500" size={40} />
                      </div>
                    </div>
                    <CardTitle className="text-2xl">{feature.title}</CardTitle>
                    <CardDescription className="text-base mt-2">{feature.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 text-left">
                      {feature.benefits.map((benefit, index) => (
                        <li key={index} className="flex items-center text-gray-600">
                          <CheckCircle2 size={18} className="mr-2 text-green-500 flex-shrink-0" />
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Additional Details */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-red-50 to-white p-8 md:p-12 rounded-lg border-2 border-red-100">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Flexible Learning Options</h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Classroom Experience</h3>
                  <p className="text-gray-600 mb-4">
                    Our offline classes provide the traditional classroom environment with direct interaction with faculty, 
                    peer learning, and immediate doubt resolution. Experience personalized attention in small batches.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-gray-600">
                      <CheckCircle2 size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                      <span>Small batch sizes for focused attention</span>
                    </li>
                    <li className="flex items-start text-gray-600">
                      <CheckCircle2 size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                      <span>Regular doubt clearing sessions</span>
                    </li>
                    <li className="flex items-start text-gray-600">
                      <CheckCircle2 size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                      <span>Comprehensive study material provided</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Digital Learning</h3>
                  <p className="text-gray-600 mb-4">
                    Online classes offer flexibility to learn from anywhere at your convenience. Access live classes, 
                    recorded lectures, and digital resources 24/7 from your device.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-gray-600">
                      <CheckCircle2 size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                      <span>Learn from the comfort of your home</span>
                    </li>
                    <li className="flex items-start text-gray-600">
                      <CheckCircle2 size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                      <span>Rewatch recorded sessions anytime</span>
                    </li>
                    <li className="flex items-start text-gray-600">
                      <CheckCircle2 size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                      <span>Access to digital library and resources</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="mt-8 text-center">
                <Link to="/contact">
                  <Button size="lg" className="bg-red-500 hover:bg-red-600 text-white px-8 py-6 text-lg">
                    Get Started Today
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Features;
